import { useCallback, useMemo } from 'react';

import { useFormContext } from '@/components/react-hook-form';
import {
  isSuggestableSuggestion,
  SuggestableSuggestion,
  SuggestedValue,
} from '@/modules/aiSuggestions/AISuggestedValue/AISuggestedValue.types';
import { SuggestableSuggestionSource } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { SuggestableKind } from '@/modules/aiSuggestions/aiSuggestions.types';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { AiSuggestionAcceptanceStatus } from '@/types/schema';

interface UseSuggestableInputHandlersParams {
  /**
   * The name of the field in the form
   */
  fieldName: string;
  /**
   * The kind of suggestion to look for
   */
  suggestionKind: SuggestableKind;
  /**
   * A function to get the suggestable suggestion for the given kind.
   * This should come from either EntitySuggestionsContext or TestamentaryEntitySuggestionsContext.
   * Can be undefined if the context is not available.
   */
  getSuggestableSuggestion:
    | ((kind: SuggestableKind) => SuggestableSuggestionSource | undefined)
    | undefined;
}

interface UseSuggestableInputHandlersResult {
  /**
   * The suggestion for the given kind, if available and is a suggestable suggestion
   */
  suggestion: SuggestableSuggestion | undefined;
  /**
   * Whether the suggestion has been acknowledged (accepted or rejected)
   */
  isAcknowledged: boolean;
  /**
   * Handler to accept a suggestion. Sets the field value and marks the suggestion as accepted.
   */
  handleAcceptSuggestion: (id: string, value: SuggestedValue['value']) => void;
  /**
   * Handler to reject a suggestion. Marks the suggestion as rejected.
   */
  handleRejectSuggestion: (id: string) => void;
}

/**
 * A hook that provides shared logic for suggestable form inputs.
 *
 * This hook extracts the common logic between SuggestableFormAwareInput and
 * SuggestableFormAwareInputTestamentaryEntity, including:
 * - Memoizing the suggestion based on the kind
 * - Handling accept/reject actions
 * - Managing form state updates
 */
export function useSuggestableInputHandlers({
  fieldName,
  suggestionKind,
  getSuggestableSuggestion,
}: UseSuggestableInputHandlersParams): UseSuggestableInputHandlersResult {
  const { setValue, setShouldBlockNavigation } = useFormContext();
  const { acknowledgedSuggestions, acknowledgeSuggestion } =
    useSuggestionAcknowledgementContext();

  const suggestion = useMemo<SuggestableSuggestion | undefined>(() => {
    if (!getSuggestableSuggestion) return undefined;
    const result = getSuggestableSuggestion(suggestionKind);
    return isSuggestableSuggestion(result) ? result : undefined;
  }, [getSuggestableSuggestion, suggestionKind]);

  const handleAcceptSuggestion = useCallback(
    (id: string, value: SuggestedValue['value']) => {
      setValue(fieldName, value, { shouldDirty: true });
      setShouldBlockNavigation(true);
      acknowledgeSuggestion({
        suggestionID: id,
        status: AiSuggestionAcceptanceStatus.Accepted,
      });
    },
    [acknowledgeSuggestion, fieldName, setShouldBlockNavigation, setValue]
  );

  const handleRejectSuggestion = useCallback(
    (id: string) => {
      setShouldBlockNavigation(true);
      acknowledgeSuggestion({
        suggestionID: id,
        status: AiSuggestionAcceptanceStatus.Rejected,
      });
    },
    [acknowledgeSuggestion, setShouldBlockNavigation]
  );

  const isAcknowledged = suggestion
    ? Boolean(acknowledgedSuggestions[suggestion.id])
    : true;

  return {
    suggestion,
    isAcknowledged,
    handleAcceptSuggestion,
    handleRejectSuggestion,
  };
}
